'use client';

import { type ProductSimple } from '@/domain/products/types';
import { Skeleton } from '@andritzot/metris-web-ui/feedback/skeleton';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useSearchParams } from 'next/navigation';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { useSolutionStore } from '../../store/solution';
import { FilterGroup, type FilterList, type Filters } from '../../types';
import { filterProduct, filterShowType } from '../../utils';
import CardsList from './CardsList';
import Header from './Header';
import InitialWizard from './InitialWizard';
import SelectedFilters from './SelectedFilters';
import SideFilter from './SideFilter';

const ProductDetailDialogWrapper = lazy(() => import('../ProductDetailDialog/ProductDetailDialogWrapper'));

interface ProductPageProps {
  products: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isInternal: boolean;
}

const ProductPage = ({ products, filtersValue, filterList, isInternal }: ProductPageProps) => {
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const filters = useSolutionStore((s) => s.filters);
  const showType = useSolutionStore((s) => s.showType);
  const industry = useSolutionStore((s) => s.industry);
  const searchValue = useSolutionStore((s) => s.searchValue);
  const layout = useSolutionStore((s) => s.layout);
  const searchParams = useSearchParams();
  const { PageViews } = usePiwikPro();

  const productId = searchParams.get('id');

  useEffect(() => {
    PageViews.trackPageView('products');
  }, [PageViews]);

  const productIdFromUrl = productId ? Number.parseInt(productId, 10) : null;
  const [prevProductId, setPrevProductId] = useState(productId);
  if (productId !== prevProductId) {
    setPrevProductId(productId);
    if (productIdFromUrl) {
      setSelectedProductId(productIdFromUrl);
    }
  }

  useEffect(() => {
    useSolutionStore.setState({ filtersValue });
  }, [filtersValue]);


  const filteredProducts = useMemo(
    () =>
      products?.filter((product) => {
        const filteredShowType = filterShowType(product, showType);
        const personas = filterProduct(product, filters, FilterGroup.USER_PERSONA);
        const industries = filterProduct(product, filters, FilterGroup.INDUSTRY, industry);
        const industryAreas = filterProduct(product, filters, FilterGroup.INDUSTRY_AREA, industry);
        const productLine = filterProduct(product, filters, FilterGroup.PRODUCT_LINE);
        const group = filterProduct(product, filters, FilterGroup.PRODUCT_GROUP);
        const benefitGroup = filterProduct(product, filters, FilterGroup.BENEFIT_GROUP);
        const benefit = filterProduct(product, filters, FilterGroup.BENEFIT);
        const lifeCycleGroup = filterProduct(product, filters, FilterGroup.PLANT_LIFECYCLE_GROUP);
        const lifeCycle = filterProduct(product, filters, FilterGroup.PLANT_LIFECYCLE);
        const businessModel = filterProduct(product, filters, FilterGroup.BUSINESS_MODEL);
        const platform = filterProduct(product, filters, FilterGroup.PLATFORM);
        const countries = filterProduct(product, filters, FilterGroup.COUNTRY);
        const regions = filterProduct(product, filters, FilterGroup.REGION);
        const portfolios = filterProduct(product, filters, FilterGroup.PORTFOLIO);
        const productUnit = filterProduct(product, filters, FilterGroup.PRODUCT_UNIT);
        const search =
          !searchValue ||
          product.product.toUpperCase().includes(searchValue.toUpperCase()) ||
          product.nickName?.toUpperCase().includes(searchValue.toUpperCase());

        return (
          filteredShowType &&
          personas &&
          industries &&
          industryAreas &&
          productLine &&
          group &&
          benefitGroup &&
          benefit &&
          lifeCycleGroup &&
          lifeCycle &&
          businessModel &&
          platform &&
          countries &&
          regions &&
          portfolios &&
          productUnit &&
          search
        );
      }) ?? [],
    [filters, industry, products, searchValue, showType],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <SideFilter
        filterList={filterList}
        filteredProducts={filteredProducts}
        filtersValue={filtersValue}
        isInternal={isInternal}
      />
      <Box sx={{ width: '100%', padding: '1rem 0.5rem', marginBottom: '1rem' }}>
        <Header />
        <Box sx={{ display: 'flex', position: 'relative', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          <SelectedFilters />
          <CardsList layout={layout} filteredProducts={filteredProducts} />
        </Box>
      </Box>
      <InitialWizard filtersValue={filtersValue} />

      {selectedProductId && (
        <Suspense fallback={<Skeleton width="100%" height={16} sx={{ mb: 1 }} />}>
          <ProductDetailDialogWrapper
            idProduct={selectedProductId}
            onClose={() => {
              setSelectedProductId(null);
              // TODO: replace search params
            }}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default ProductPage;
